import { makeAutoObservable } from 'mobx';
import groupBy from 'lodash/groupBy';

import type { IndividualReport, EntityFileData, FileViewModel } from 'chaika-frontend-types/frontend-cms';

import { IndividualReportFilterParams, IndividualReportsList } from './IndividualReportsList';

export class IndividualReportsStore {
    public static Name = 'individualReportsStore' as const;

    public individualReports = new IndividualReportsList();
    private files: Map<IndividualReport['id'], FileViewModel[]> = new Map();

    constructor() {
        makeAutoObservable(this);
    }

    public setIndividualReports(individualReports: IndividualReport[]): void {
        this.individualReports.set(individualReports);
    }

    public addIndividualReport(...individualReport: IndividualReport[]): void {
        this.individualReports.add(individualReport);
    }

    public updateIndividualReport(individualReportId: string, individualReport: Partial<IndividualReport>): void {
        this.individualReports.update(individualReportId, individualReport);
    }

    public getIndividualReports(filterParams: IndividualReportFilterParams = {}): IndividualReport[] {
        return this.individualReports.getFilteredValues(filterParams);
    }

    public getActiveIndividualReports(filterParams: IndividualReportFilterParams): IndividualReport[] {
        return this.getIndividualReports(filterParams).filter(({ id }) => this.hasFiles(id));
    }

    public hasActiveIndividualReports(filterParams: IndividualReportFilterParams = {}): boolean {
        const individualReports = this.getIndividualReports(filterParams);
        return individualReports.length > 0 && individualReports.some(({ id }) => this.hasFiles(id));
    }

    public getIndividualReport(individualReportId: string): IndividualReport {
        return this.individualReports.getExisting(individualReportId);
    }

    public setFiles(files: EntityFileData[]): void {
        const groupedFiles = groupBy(files, 'entityId');

        Object.entries(groupedFiles).forEach(([id, files]) => {
            this.files.set(id, files);
        });
    }

    public addFile(individualReportId: string, file: FileViewModel): void {
        const currentFiles = this.files.get(individualReportId) ?? [];
        this.files.set(individualReportId, currentFiles.concat(file));
    }

    public deleteFile(individualReportId: string, path: string): void {
        const currentFiles = this.files.get(individualReportId) ?? [];
        this.files.set(
            individualReportId,
            currentFiles.filter(it => it.path !== path),
        );
    }

    public hasFiles(individualReportId: string): boolean {
        return this.getFiles(individualReportId).length > 0;
    }

    public getFiles(individualReportId: string): FileViewModel[] {
        return this.files.get(individualReportId) ?? [];
    }

    public reset(): void {
        this.individualReports.reset();
        this.files = new Map();
    }
}

export const individualReportsStore = new IndividualReportsStore();
